import { type CSSProperties, type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form as AntForm, Flex, type FormInstance, type FormProps, theme } from "antd";
import { createStyles } from "antd-style";

import { Typography } from "@av/components/atoms/typography/Typography";
import type { IEntityPermissions } from "@av/config/api-config/permissions/entity-permissions.interface";
import { FormItem } from "./form-item/FormItem";
import { FormList } from "./form-list/FormList";
import { FormMetaContextContainer } from "./meta-context/FormMetaContextContainer";
import { useCustomWatch } from "./use-watch/use-watch";
import { useFieldDataMapper } from "./utils/useFieldDataMapper";

import type { FieldName, FormExternalError } from "./types";
import type { FieldData } from "rc-field-form/es/interface";
import type { NamePath } from "rc-field-form/lib/interface";

interface StyledFormProps<T> extends FormProps {
  children: ReactNode;
  style?: CSSProperties;
  maxWidth?: string;
  form?: FormInstance<T>;
  serverErrors?: FormExternalError[];
  permissions?: IEntityPermissions;
}

const useStyles = createStyles(({ token, css }, { maxWidth }: { maxWidth?: string }) => ({
  card: css`
    border-radius: ${token.borderRadiusLG}px;
    margin-bottom: ${token.marginLG}px;
    max-width: ${maxWidth ?? "1024px"};
    padding: 0;
  `,
}));

function FormErrorHandler<T>({
  children,
  serverErrorCache = {},
}: Pick<StyledFormProps<T>, "children"> & { serverErrorCache: ServerErrorCache }) {
  const formInstance = AntForm.useFormInstance();
  const { mapExternalErrorsToFieldData } = useFieldDataMapper();

  /**
   * Map server errors to antd errors with translation
   */
  useEffect(() => {
    const displayedServerErrors: FormExternalError[] = [];

    Object.values(serverErrorCache).forEach((e) => {
      if (!e.isTouched) {
        displayedServerErrors.push(...e.errors);
      }
    });

    if (displayedServerErrors.length > 0) {
      const fields = mapExternalErrorsToFieldData(displayedServerErrors);

      formInstance.setFields(fields);
    }
  }, [formInstance, mapExternalErrorsToFieldData, serverErrorCache]);

  return <>{children}</>;
}

type ServerErrorCache = {
  [key: FieldName]: {
    errors: FormExternalError[];
    isTouched: boolean;
  };
};

/**
 * Stable constant empty array as default array
 */
const EMPTY_SERVER_ERRORS: FormExternalError[] = [];

function FormWithProvider<T>(props: StyledFormProps<T>) {
  const {
    style,
    maxWidth,
    onFieldsChange,
    serverErrors = EMPTY_SERVER_ERRORS,
    permissions,
    requiredMark: requiredMarkProp,
    ...restProps
  } = props;
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { styles } = useStyles({ maxWidth });
  const { mapFieldsToSubmittedObject, joinName, splitName } = useFieldDataMapper();
  const [serverErrorCache, setServerErrorCache] = useState<ServerErrorCache>({});
  // Create form instance or reuse passed down form prop
  const [formInstance] = AntForm.useForm(props.form);

  if (!Array.isArray(serverErrors)) {
    throw Error("Server error needs to be an array");
  }

  /**
   * Cache the server errors in memory.
   * These will be used to clear the errors when fields are touched.
   */
  useEffect(() => {
    setServerErrorCache((prevServerErrorCache) => {
      const nextServerErrorCache = serverErrors.reduce((acc, curr) => {
        /**
         * Same field can have multiple errors
         */
        if (acc[curr.field]) {
          acc[curr.field].errors.push(curr);
        } else {
          acc[curr.field] = {
            errors: [curr] as FormExternalError[],
            isTouched: prevServerErrorCache[curr.field]?.isTouched ?? false,
          };
        }

        return acc;
      }, {} as ServerErrorCache);

      const removedFieldNames: FieldData[] = [];

      Object.keys(prevServerErrorCache).forEach((fieldName) => {
        if (!nextServerErrorCache[fieldName]) {
          removedFieldNames.push({
            name: splitName(fieldName) ?? (fieldName as NamePath),
            errors: [],
            warnings: [],
          });
        }
      });

      if (removedFieldNames.length > 0) {
        formInstance.setFields(removedFieldNames);
      }

      return nextServerErrorCache;
    });
  }, [formInstance, serverErrors, splitName]);

  const customOnFinish = (data: T) => {
    props.onFinish?.(mapFieldsToSubmittedObject(data as object, permissions));

    setServerErrorCache((prev) =>
      Object.keys(serverErrorCache).reduce((acc, curr) => {
        acc[curr] = {
          ...prev[curr],
          isTouched: false,
        };

        return acc;
      }, {} as ServerErrorCache)
    );
  };

  /**
   * On field change, any server errors will be cleared if the field is touched
   */
  const handleFieldsChange = (changedFields: FieldData[], allFields: FieldData[]) => {
    const serverErrorCopy = { ...serverErrorCache };

    let isChanged = false;

    changedFields.forEach((field) => {
      const antdFieldName = field.name;
      const fieldName = joinName(field.name);

      if (field.touched && serverErrorCache[fieldName]) {
        formInstance.setFields([{ name: antdFieldName, errors: [] }]);
        serverErrorCopy[fieldName].isTouched = true;
        isChanged = true;
      }
    });

    if (isChanged) {
      setServerErrorCache(serverErrorCopy);
    }

    onFieldsChange?.(changedFields, allFields);
  };

  const requiredMark =
    requiredMarkProp === "optional"
      ? (label: ReactNode, { required }: { required: boolean }) => {
          if (required) {
            return label;
          }

          return (
            <Flex vertical align="flex-start" gap={0}>
              {label}
              <Typography.Text
                testIdPrefix="form-label-optional"
                style={{
                  color: token.cyan7,
                  lineHeight: 1.2,
                }}
              >
                {t("common.components.atoms_label_optional")}
              </Typography.Text>
            </Flex>
          );
        }
      : (requiredMarkProp ?? false);

  return (
    <FormMetaContextContainer permissions={permissions}>
      <AntForm
        form={formInstance}
        labelCol={{ span: 10 }}
        wrapperCol={{ span: 16 }}
        autoComplete="off"
        layout="horizontal"
        labelAlign="left"
        size="large"
        colon={false}
        requiredMark={requiredMark}
        className={styles.card}
        style={style}
        {...restProps}
        onFinish={customOnFinish}
        onFieldsChange={handleFieldsChange}
        initialValues={restProps.initialValues}
      >
        <FormErrorHandler serverErrorCache={serverErrorCache}>{props.children}</FormErrorHandler>
      </AntForm>
    </FormMetaContextContainer>
  );
}

export const Form = Object.assign(FormWithProvider, {
  Item: FormItem,
  useForm: AntForm.useForm,
  useFormInstance: AntForm.useFormInstance,
  useWatch: useCustomWatch,
  List: FormList,
});
